import {
  Component,
  h,
  Host,
  Element,
  Prop,
  Watch,
  EventEmitter,
  Event,
  State,
  Method,
  ComponentInterface,
  Listen,
} from '@stencil/core';
import { getDirectChildren, getSiblings } from '../../utils/dom';
import { displayTransition } from '../../utils/transitions';
import { getTabbableElements } from '../../utils/tabbable';
import type { NavItemEventDetail } from '../../interface';

/**
 * Nav items to be used with the various nav items.
 * [Globla-Nav](/story/compounds-global-nav)
 * [Menu-Drawer](/story/components-menu-drawer)
 * [Dropdown](/story/components-dropdown)
 *
 * @slot icon-start - an icon at the start of the main control
 * @slot icon-end - an icon at the end of the main control
 * @slot secondary - a content panel in which you can place any html to display on control click
 * @slot - use the default slot for either `<a>` or text (if constructing a secondary content panel or using `href` directly)
 * @part ctrl - the controlling `<a>` or `<button>` element
 * @part ctrl--button - the controlling `<button>` element
 * @part ctrl--anchor - the controlling <a> element
 * @part ctrl-wrapper - the wrapping div around the control (shadow `<button>` / `<a>` or slotted `<a>`)
 * @part secondary-wrapper - the div surrounding slotted secondary content
 * @part secondary-mask - the div that is added when secondary content is shown
 */
@Component({
  tag: 'nano-nav-item',
  styleUrl: 'nav-item.scss',
  shadow: true,
})
export class NavItem implements ComponentInterface {
  private hasSecondarySlot: boolean = false;
  private hasAnchorEle: boolean = false;
  private secondaryDiv: HTMLDivElement;
  private btn: HTMLButtonElement | HTMLAnchorElement;
  private fromHover: boolean;
  private animating: boolean = false;
  private waitHide: number;
  private waitShow: number;
  private globalNavEle?: HTMLNanoGlobalNavElement;

  @State() didOpen: boolean = false;
  @State() isInGlobalNav: boolean = false;
  @State() isInMenuDrawer: boolean = false;
  @State() isInMenu: boolean = false;
  @State() hasFocus: boolean = false;
  @Element() private el: HTMLNanoNavItemElement;

  /**
   * Href to link to
   */
  @Prop({ mutable: true }) href: string = null;

  /**
   * Target to use for links
   */
  @Prop() target: '_self' | '_blank' | '_parent' | '_top' = '_self';

  /**
   * Disable the nav item
   */
  @Prop() disabled: boolean = false;

  /**
   * Whether a secondary menu should be or is currently open
   */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  /**
   * Whether this menu item is currently 'selected'
   */
  @Prop({ reflect: true }) selected: boolean = false;

  /**
   * Screen pixel width to activate the secondary menu
   */
  @Prop() secondaryActiveWidth: number = 0;

  /**
   * Secondary fallback function for when the secondary menu is no longer active.
   */
  @Prop() secondaryFallback = () => {};

  /**
   * Whether secondary menus should close on blur
   */
  @Prop() closeOnBlur: boolean = true;

  /**
   * Will show an indicator badge - only when placed in a nano-menu-draw
   */
  @Prop() notification: boolean = false;

  /**
   * Emitted when the nav item closes it's secondary navigation.
   */
  @Event() nanoClose!: EventEmitter<NavItemEventDetail>;

  /**
   * Emitted when the nav item opens it's secondary navigation.
   */
  @Event() nanoOpen!: EventEmitter<NavItemEventDetail>;

  /**
   * Emitted when the nav item is about to open it's secondary navigation.
   */
  @Event() nanoOpening!: EventEmitter<NavItemEventDetail>;

  /**
   * Emitted when the nav item is about to close it's secondary navigation.
   */
  @Event() nanoClosing!: EventEmitter<NavItemEventDetail>;

  /**
   * Emitted when the nav item loses focus.
   */
  @Event() nanoBlur!: EventEmitter<void>;

  /**
   * Emitted when the nav item has focus.
   */
  @Event() nanoFocus!: EventEmitter<void>;

  /**
   * Sets focus on the first button or anchor present. Use this method instead of the global
   * `element.focus()`.
   */
  @Method()
  async setFocus() {
    if (!this.btn) return;
    this.btn.focus();
  }

  /**
   * Public open watcher
   */
  @Watch('open')
  openChange() {
    this.open ? this.openSecondary() : this.closeSecondary();
  }

  /**
   * Internal open watcher
   */
  @Watch('didOpen')
  didOpenChange() {
    if (!this.hasSecondarySlot) return;

    if (!this.didOpen) {
      /* the secondadry panel is not closing via hover,
      remove blur events, add focus back to originating button */
      setTimeout(() => {
        if (
          !this.fromHover &&
          (!document.activeElement || document.activeElement === document.body)
        )
          this.btn.focus({ preventScroll: true });
        this.el.tabIndex = null;
        this.nanoClose.emit({ secondaryMenu: this.secondaryDiv });
      }, 50);
    } else {
      /* the secondadry panel is not opening via hover,
      scroll content into view, focus on it and add blur events */
      if (!this.fromHover) {
        const panelio = new (window as any).IntersectionObserver(
          (data: IntersectionObserverEntry[]) => {
            if (data[0].boundingClientRect.top < 0) {
              this.secondaryDiv.scrollIntoView({
                behavior: 'smooth',
              });
            }
            panelio.disconnect();
          },
          { threshold: 1 }
        );
        panelio.observe(this.secondaryDiv);
        this.secondaryDiv.focus({ preventScroll: true });

        const focusableChild = getTabbableElements(this.secondaryDiv, true);
        if (focusableChild[0]) focusableChild[0].focus();

        this.el.tabIndex = -1;
        this.nanoOpen.emit({ secondaryMenu: this.secondaryDiv });
      }
    }
  }

  @Listen('click', { target: 'body' })
  @Listen('keyup', { target: 'body' })
  handleClose(e: MouseEvent | KeyboardEvent) {
    if (!this.didOpen || !this.hasSecondarySlot) return;
    if (!e.composedPath().some((el) => el === this.secondaryDiv))
      this.toggleSecondary();
  }

  private closeSecondary = async () => {
    if (!this.hasSecondarySlot || !this.didOpen) return;
    this.open = false;
    await displayTransition(this.secondaryDiv, 'open', false);
    this.nanoClosing.emit({ secondaryMenu: this.secondaryDiv });
    this.didOpen = false;
  };

  private openSecondary = async () => {
    if (window.innerWidth < this.secondaryActiveWidth) {
      this.secondaryFallback.call(this);
      return;
    }

    if (!this.hasSecondarySlot || this.didOpen) return;
    this.open = true;
    await displayTransition(this.secondaryDiv, 'open', true);
    this.nanoOpening.emit({ secondaryMenu: this.secondaryDiv });
    this.didOpen = true;
  };

  private toggleSecondary = () => {
    if (!this.hasSecondarySlot) return;
    if (this.didOpen) this.closeSecondary();
    else this.openSecondary();
  };

  private handleFocus = () => {
    this.hasFocus = true;
    // opens secondary panel when activated via hover
    if (this.href && !this.animating && this.hasSecondarySlot)
      this.openSecondary();
    this.nanoFocus.emit();
  };

  private handleHostBlur = () => {
    if (this.animating) return;

    // tab index is added to host when secondary panel opens
    // to return focus after secondary panel closes. Remove now if it was added
    this.el.removeAttribute('tabindex');
    this.hasFocus = false;
    this.nanoBlur.emit();
  };

  // direct host clicks to internal button
  private handleHostClick = (ev: MouseEvent) => {
    if (!this.hasAnchorEle) return;
    const foundlink = (ev.target as HTMLElement).closest('a, button');
    if (!foundlink) this.btn.click();
  };

  // handles secondary menu hover behaviour.
  private handleMouseEnter = async () => {
    if (
      !this.href ||
      this.animating ||
      !this.hasSecondarySlot ||
      (this.globalNavEle && !this.globalNavEle.classList.contains('ready'))
    )
      return;

    // if there are sibling secondary panels currently open,
    // we need to wait before opening otherwise we get unwanted overlapping
    let timeToWait = 0;
    if (getSiblings(this.el, '.secondary-open.has-secondary').length)
      timeToWait = 300;

    clearTimeout(this.waitHide);
    this.fromHover = true;
    this.animating = true;
    this.waitShow = window.setTimeout(
      async () => await this.openSecondary(),
      timeToWait
    );
    this.animating = false;
  };

  // handles secondary menu hover-out behaviour.
  private handleMouseLeave = () => {
    if (!this.href) return;
    clearTimeout(this.waitShow);
    this.fromHover = true;
    this.animating = true;

    // prevent closing immediately - makes navigating to items within more forgiving
    this.waitHide = window.setTimeout(
      async () => await this.closeSecondary(),
      300
    );
    this.animating = false;
  };

  private handleClick = () => {
    this.fromHover = false;
    if (this.hasSecondarySlot) this.toggleSecondary();
    else this.el.querySelector<HTMLElement>('*')?.click();
  };

  componentWillLoad() {
    const secondaryEle = this.el.querySelector('[slot="secondary"]');
    this.hasSecondarySlot = !!secondaryEle;

    const link = getDirectChildren(this.el, 'a, button')[0] as
      | HTMLAnchorElement
      | HTMLButtonElement;
    this.hasAnchorEle = !!link;

    if (link) {
      this.href = (link as HTMLAnchorElement).href
        ? (link as HTMLAnchorElement).href
        : null;
      this.btn = link;
      if (this.hasSecondarySlot)
        this.btn.addEventListener('click', this.toggleSecondary);

      this.btn.addEventListener('focus', this.handleFocus);
      this.btn.addEventListener('blur', this.handleHostBlur);
    }
  }

  connectedCallback() {
    this.isInGlobalNav = false;
    this.isInMenuDrawer = false;
    this.globalNavEle = this.el.closest('nano-global-nav');

    this.isInMenuDrawer = !!this.el.closest('nano-menu-drawer');
    this.isInMenu = !!this.el.closest('nano-menu');
    this.isInGlobalNav =
      this.el.parentElement === this.globalNavEle ||
      !!this.el.closest('.global-nav');
  }

  render() {
    return (
      <Host
        class={{
          'has-secondary': this.hasSecondarySlot,
          'secondary-open': this.open,
          selected: this.selected,
          'has-focus': this.hasFocus,
          disabled: this.disabled,
          'nano-global-nav': this.isInGlobalNav,
          'nano-menu-drawer': this.isInMenuDrawer,
          'nano-menu': this.isInMenu,
        }}
        onBlur={this.handleHostBlur}
        dir={(this.el.ownerDocument as Document).dir === 'rtl' ? 'rtl' : null}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onClick={this.handleHostClick}
        role="menuitem"
      >
        <div
          class={{
            'nav-item': true,
            'secondary-open': this.open,
            selected: this.selected,
          }}
          part="ctrl-wrapper"
        >
          {this.href && !this.hasAnchorEle && !this.disabled && (
            <a
              part="ctrl ctrl--anchor"
              target={this.target}
              ref={(a) => (this.btn = a)}
              href={this.href}
              onFocus={this.handleFocus}
              class={{
                notification: this.notification,
              }}
            >
              <slot name="icon-start" />
              <span class="text">
                <slot />
              </span>
              <slot name="icon-end" />
            </a>
          )}
          {((!this.hasAnchorEle && !this.href) || this.disabled) && (
            <button
              part="ctrl ctrl--button"
              ref={(btn) => (this.btn = btn)}
              onClick={this.handleClick}
              onFocus={this.handleFocus}
              disabled={this.disabled}
              class={{
                notification: this.notification,
              }}
            >
              <slot name="icon-start" />
              <span class="text">
                <slot />
              </span>
              <slot name="icon-end" />
            </button>
          )}
          {this.hasAnchorEle && !this.disabled && (
            <div class="link">
              <slot name="icon-start" />
              <slot />
              <slot name="icon-end" />
            </div>
          )}
          {this.hasSecondarySlot && !this.disabled && (
            <div
              class="secondary-menu"
              ref={(div) => (this.secondaryDiv = div)}
              tabIndex={-1}
            >
              <div class="secondary-menu-content" part="secondary-wrapper">
                <slot name="secondary" />
              </div>
              <div
                class="secondary-menu-mask"
                onClick={this.closeSecondary}
                part="secondary-mask"
              ></div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
